import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  PanchangDay,
  PanchangPeriod,
  PanchangSequenceEntry,
  PanchangDevataEntry,
} from "@vedaunion/shared";
import { Term, fmtTime, qualityClass, activeIn } from "./panchangUtils";
import { SunIcon, MoonIcon } from "./lunarIcons";

/**
 * Full per-day panchāṅga. Three clearly-separated sections:
 *   1. "Qualities of the day" — UNIVERSAL (tithi, nakṣatra, yoga, karaṇa,
 *      samvat). True everywhere; transition times shown in the panchang tz.
 *   2. "Festivals & observances" — named days with neutral descriptions.
 *   3. "Timings · {location}" — LOCAL (sun/moon, auspicious + inauspicious
 *      windows, choghaḍiyā). Explicitly labelled as computed for the location.
 */
export function PanchangDayPanel({
  day,
  timezone,
  dateLabel,
  showNow = false,
}: {
  day: PanchangDay;
  timezone: string;
  dateLabel: string;
  /** When true (the selected day is today), show the live "right now" strip. */
  showNow?: boolean;
}) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!showNow) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [showNow]);

  if (!day.available) {
    return (
      <div className="pa-panel">
        <p className="pa-eyebrow">{dateLabel}</p>
        <p className="pa-muted">
          The panchāṅga for this day isn't cached yet. Days are fetched daily
          within a rolling window — nearby dates load first.
        </p>
      </div>
    );
  }

  // Live "right now" data (only meaningful when showNow / today).
  const activeInaus = showNow ? activeIn(day.inauspicious, now) : null;
  const activeAus = showNow ? activeIn(day.auspicious, now) : null;
  const allChogh = [...(day.choghadiya?.day ?? []), ...(day.choghadiya?.night ?? [])];
  const curChogh = showNow
    ? allChogh.find((s) => now >= new Date(s.start) && now < new Date(s.end))
    : undefined;
  const nextAus = showNow
    ? day.auspicious
        .flatMap((a) => a.segments.map((s) => ({ name: a.name, ...s })))
        .filter((s) => new Date(s.start) > now)
        .sort((a, b) => +new Date(a.start) - +new Date(b.start))[0]
    : undefined;

  const seqRow = (entry: PanchangSequenceEntry, withLord = false) => (
    <li key={`${entry.name.en}-${entry.start ?? ""}`} className="pa-seq-row">
      <span className="pa-seq-name">
        <Term value={entry.name} />
        {entry.paksha ? <span className="pa-sub"> · <Term value={entry.paksha} /></span> : null}
        {withLord && entry.lord ? <span className="pa-sub"> · <Term value={entry.lord} /></span> : null}
      </span>
      <span className="pa-seq-time">
        {fmtTime(entry.start, timezone)}–{fmtTime(entry.end, timezone)}
      </span>
    </li>
  );

  const devataItem = (label: string, entry: PanchangDevataEntry) => (
    <li className="pa-devata-item">
      <p className="pa-devata-label">{label}</p>
      <p className="pa-devata-deity">
        {entry.deity.iast}
        {entry.deity.deva ? <span className="pa-deva"> {entry.deity.deva}</span> : null}
      </p>
      <p className="pa-devata-note">{entry.note}</p>
    </li>
  );

  const periodList = (periods: PanchangPeriod[]) =>
    periods.map((p) => (
      <li key={p.key} className={`pa-period pa-period--${p.type}`}>
        <span className="pa-period-name"><Term value={p.name} /></span>
        <span className="pa-period-time">
          {p.segments.map((s, i) => (
            <span key={i} className="pa-period-seg">
              {fmtTime(s.start, timezone)}–{fmtTime(s.end, timezone)}
            </span>
          ))}
        </span>
      </li>
    ));

  return (
    <div className="pa-panel">
      <div className="pa-panel-head">
        <p className="pa-eyebrow">{dateLabel}</p>
        <h3 className="pa-panel-title">
          <Term value={day.vaara} />
        </h3>
        {day.markers.length > 0 && (
          <div className="pa-marker-row">
            {day.markers.map((m) => (
              <span key={m.key} className={`pa-badge pa-badge--${m.kind}`}>
                {m.label}
                {m.deva ? <span className="pa-deva"> {m.deva}</span> : null}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ---- RIGHT NOW (today only) ---- */}
      {showNow && (
        <div className="pa-now">
          <p className="pa-now-label">
            Right now <span className="pa-loc">· {day.locationName}</span>
          </p>
          <div className="pa-now-pills">
            {activeInaus ? (
              <span className="pa-pill pa-pill--bad">
                In <Term value={activeInaus.name} /> until{" "}
                {fmtTime(activeInaus.segments.find((s) => now >= new Date(s.start) && now < new Date(s.end))?.end, timezone)}
              </span>
            ) : activeAus ? (
              <span className="pa-pill pa-pill--good">
                In <Term value={activeAus.name} /> until{" "}
                {fmtTime(activeAus.segments.find((s) => now >= new Date(s.start) && now < new Date(s.end))?.end, timezone)}
              </span>
            ) : (
              <span className="pa-pill pa-pill--neutral">No special period active</span>
            )}
            {curChogh && (
              <span className={`pa-pill pa-pill--${qualityClass(curChogh.type)}`}>
                Choghaḍiyā: <Term value={curChogh.name} /> ({curChogh.type})
              </span>
            )}
          </div>
          {nextAus && (
            <p className="pa-next">
              Next auspicious: <Term value={nextAus.name} /> at {fmtTime(nextAus.start, timezone)}
            </p>
          )}
        </div>
      )}

      {/* ---- UNIVERSAL ---- */}
      <section className="pa-section">
        <h4 className="pa-section-title">Qualities of the day</h4>
        <div className="pa-seq-grid">
          {day.tithi.length > 0 && (
            <div className="pa-seq-block">
              <p className="pa-seq-label">Tithi</p>
              <ul className="pa-seq-list">{day.tithi.map((t) => seqRow(t))}</ul>
            </div>
          )}
          {day.nakshatra.length > 0 && (
            <div className="pa-seq-block">
              <p className="pa-seq-label">Nakṣatra</p>
              <ul className="pa-seq-list">{day.nakshatra.map((n) => seqRow(n, true))}</ul>
            </div>
          )}
          {day.yoga.length > 0 && (
            <div className="pa-seq-block">
              <p className="pa-seq-label">Yoga</p>
              <ul className="pa-seq-list">{day.yoga.map((y) => seqRow(y))}</ul>
            </div>
          )}
          {day.karana.length > 0 && (
            <div className="pa-seq-block">
              <p className="pa-seq-label">Karaṇa</p>
              <ul className="pa-seq-list">{day.karana.map((k) => seqRow(k))}</ul>
            </div>
          )}
        </div>
        {day.samvat.length > 0 && (
          <p className="pa-samvat">
            {day.samvat.map((s) => (
              <span key={s.era} className="pa-samvat-item">
                {s.era}: {s.yearName ? `${s.yearName} ` : ""}
                {s.year ?? ""}{s.month ? ` · ${s.month}` : ""}
              </span>
            ))}
          </p>
        )}
      </section>

      {/* ---- FESTIVALS & OBSERVANCES ---- */}
      {day.festivals.length > 0 && (
        <section className="pa-section">
          <h4 className="pa-section-title">Festivals &amp; observances</h4>
          <ul className="pa-festival-list">
            {day.festivals.map((f) => (
              <li key={f.id} className="pa-festival-item">
                <div className="pa-festival-head">
                  <span className={`pa-badge pa-badge--${f.kind}`}>{f.kind}</span>
                  <span className="pa-festival-name">
                    {f.name}
                    {f.deva ? <span className="pa-deva"> {f.deva}</span> : null}
                  </span>
                </div>
                {f.description ? (
                  <p className="pa-festival-desc">{f.description}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---- DEDICATION (DEVATĀ) ---- */}
      {day.devata && (
        <section className="pa-section">
          <h4 className="pa-section-title">Dedication · Devatā</h4>
          <ul className="pa-devata-list">
            {devataItem("Weekday", day.devata.vara)}
            {day.devata.tithi && devataItem("Lunar day (tithi)", day.devata.tithi)}
            {day.devata.nakshatra && devataItem("Nakṣatra", day.devata.nakshatra)}
          </ul>
        </section>
      )}

      {/* ---- RELATED LIBRARY DOCUMENTS ---- */}
      {day.relatedDocuments.length > 0 && (
        <section className="pa-section">
          <h4 className="pa-section-title">Related from the Library</h4>
          <ul className="pa-related-list">
            {day.relatedDocuments.map((d) => (
              <li key={d.slug} className="pa-related-item">
                <Link to={`/library/${d.slug}`} className="pa-related-link">
                  <span className="pa-related-title">
                    {d.title}
                    {d.type ? <span className="pa-related-type">{d.type}</span> : null}
                  </span>
                  {d.summary ? <span className="pa-related-summary">{d.summary}</span> : null}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---- LOCAL ---- */}
      <section className="pa-section">
        <h4 className="pa-section-title">
          Timings <span className="pa-loc">· {day.locationName}</span>
        </h4>
        <p className="pa-suntimes pa-suntimes--block">
          <span className="pa-suntime">
            <SunIcon size={14} className="pa-suntime-icon" />
            Sunrise {fmtTime(day.sunrise, timezone)} · Sunset {fmtTime(day.sunset, timezone)}
          </span>
          {day.moonrise ? (
            <span className="pa-suntime">
              <MoonIcon size={14} className="pa-suntime-icon" />
              Moonrise {fmtTime(day.moonrise, timezone)} · Moonset {fmtTime(day.moonset, timezone)}
            </span>
          ) : null}
        </p>

        <div className="pa-period-cols">
          {day.auspicious.length > 0 && (
            <div>
              <p className="pa-seq-label pa-seq-label--good">Auspicious</p>
              <ul className="pa-period-list">{periodList(day.auspicious)}</ul>
            </div>
          )}
          {day.inauspicious.length > 0 && (
            <div>
              <p className="pa-seq-label pa-seq-label--bad">Inauspicious</p>
              <ul className="pa-period-list">{periodList(day.inauspicious)}</ul>
            </div>
          )}
        </div>

        {day.choghadiya && (
          <div className="pa-chogh">
            <p className="pa-seq-label">Choghaḍiyā</p>
            <div className="pa-chogh-cols">
              <div>
                <p className="pa-chogh-head">Day</p>
                <ul className="pa-chogh-list">
                  {day.choghadiya.day.map((s, i) => (
                    <li key={`d${i}`} className={`pa-chogh-slot pa-chogh-slot--${qualityClass(s.type)}`}>
                      <span><Term value={s.name} /></span>
                      <span className="pa-chogh-time">{fmtTime(s.start, timezone)}–{fmtTime(s.end, timezone)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="pa-chogh-head">Night</p>
                <ul className="pa-chogh-list">
                  {day.choghadiya.night.map((s, i) => (
                    <li key={`n${i}`} className={`pa-chogh-slot pa-chogh-slot--${qualityClass(s.type)}`}>
                      <span><Term value={s.name} /></span>
                      <span className="pa-chogh-time">{fmtTime(s.start, timezone)}–{fmtTime(s.end, timezone)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>

      <p className="pa-footnote">
        Universal qualities (tithi, nakṣatra, yoga, karaṇa) apply everywhere.
        Timings are computed for {day.locationName}.
      </p>
    </div>
  );
}
